import { combineEpics, ofType } from "redux-observable";
import { mergeMap, catchError } from "rxjs/operators";
import { of } from "rxjs";
import {
  propertyRequest,
  propertySuccess,
  propertyFailure,
  propertySignatureRequest,
  propertySignatureSuccess,
  propertySignatureFailure,
  propertyDetailRequest,
  propertyDetailSuccess,
  propertyDetailFailure,
  propertyFilterRequest,
  propertyFilterSuccess,
  propertyFilterFailure,
  fetchTagsRequest,
  fetchTagsSuccess,
  fetchTagsFailure,
  savePropertyRequest,
  savePropertySuccess,
  savePropertyFailure,
  savePropertyImageRequest,
  savePropertyImageSuccess,
  savePropertyImageFailure,
  propertiesByUserRequest,
  propertiesByUserSuccess,
  propertiesByUserFailure,
  myPropertiesRequest,
  myPropertiesSuccess,
  myPropertiesFailure,
  propertyDeleteRequest,
  propertyDeleteSuccess,
  propertyDeleteFailure,
  propertyHideRequest,
  propertyHideSuccess,
  propertyHideFailure,
  editPropertyRequest,
  editPropertySuccess,
  editPropertyFailure,
  explorePropertyRequest,
  explorePropertySuccess,
  explorePropertyFailure,
  aiSearchRequest,
  aiSearchSuccess,
  aiSearchFailure,
  deletePropertyImageRequest,
  deletePropertyImageSuccess,
  deletePropertyImageFailure,
  getCompareProperties,
  comparePropertiesSuccess,
  comparePropertiesFailure,
  markPropertyAsFavoriteRequest,
  markPropertyAsFavoriteSuccess,
  markPropertyAsFavoriteFailure,
  unMarkPropertyAsFavoriteRequest,
  unMarkPropertyAsFavoriteSuccess,
  unMarkPropertyAsFavoriteFailure,
  favouritePropertiesRequest,
  favouritePropertiesSuccess,
  favouritePropertiesFailure,
  addCountAsPropertyViewRequest,
  addCountAsPropertyViewSuccess,
  addCountAsPropertyViewFailure,
  countryBasedPropertyRequest,
  countryBasedPropertySuccess,
  countryBasedPropertyFailure
} from "./property";
import {
  observablePOST,
  observableGET,
  observableDELETE,
  observablePATCH,
  observablePUT
} from "@/api/apiClient";
import {
  decreaseTotalAvailableListings,
  increaseTotalAvailableListings
} from "@/features/user/duck/user";

const fetchDataPropertyEpic = (action$) =>
  action$.pipe(
    ofType(propertyRequest.type),
    mergeMap((action) => {
      const { params } = action.payload;
      return observableGET(`/property`, { params }).pipe(
        mergeMap((response) =>
          of(propertySuccess({ ...response.data, currentPage: params.page }))
        ),
        catchError((error) => of(propertyFailure(error)))
      );
    })
  );
const fetchDataPropertySignatureEpic = (action$) =>
  action$.pipe(
    ofType(propertySignatureRequest.type),
    mergeMap((action) => {
      const { params } = action.payload;
      return observableGET(`/property`, {
        params: { size: 10, ...params }
      }).pipe(
        mergeMap((response) =>
          of(
            propertySignatureSuccess({
              ...response.data,
              currentPage: params.page
            })
          )
        ),
        catchError((error) => of(propertySignatureFailure(error)))
      );
    })
  );

const fetchDataExplorePropertyEpic = (action$) =>
  action$.pipe(
    ofType(explorePropertyRequest.type),
    mergeMap((action) => {
      const { params } = action.payload;
      return observableGET(`/property`, { params }).pipe(
        mergeMap((response) => of(explorePropertySuccess(response.data))),
        catchError((error) => of(explorePropertyFailure(error)))
      );
    })
  );

const fetchDataPropertyFilterEpic = (action$) =>
  action$.pipe(
    ofType(propertyFilterRequest.type),
    mergeMap((action) => {
      const { params } = action.payload;
      return observableGET(`/property`, { params }).pipe(
        mergeMap((response) => of(propertyFilterSuccess({ ...response.data }))),
        catchError((error) => of(propertyFilterFailure(error)))
      );
    })
  );

const fetchDataPropertyDetailEpic = (action$) =>
  action$.pipe(
    ofType(propertyDetailRequest.type),
    mergeMap((action) => {
      const propertyId = action.payload;
      return observableGET(`/property/${propertyId}`).pipe(
        mergeMap((response) => of(propertyDetailSuccess(response.data))),
        catchError((error) => of(propertyDetailFailure(error)))
      );
    })
  );

const fetchTagEpic = (action$) => {
  return action$.pipe(
    ofType(fetchTagsRequest.type),
    mergeMap((action) => {
      return observableGET(`/property-additional-features`, {
        body: action.payload
      }).pipe(
        mergeMap((response) => {
          return of(fetchTagsSuccess(response.data));
        }),
        catchError((error) => of(fetchTagsFailure(error)))
      );
    })
  );
};

const savePropertyEpic = (action$) => {
  return action$.pipe(
    ofType(savePropertyRequest.type),
    mergeMap((action) => {
      return observablePOST(`/property`, {
        body: action.payload
      }).pipe(
        mergeMap((response) => {
          return of(
            savePropertySuccess(response.data),
            savePropertyImageRequest(response.data.uuid),
            decreaseTotalAvailableListings()
          );
        }),
        catchError((error) => of(savePropertyFailure(error)))
      );
    })
  );
};

const savePropertyImageEpic = (action$, state$) => {
  return action$.pipe(
    ofType(savePropertyImageRequest.type),
    mergeMap((action) => {
      const imageList = state$.value?.property?.imageList;

      const imagesToUpload = imageList.filter((image) => image.originFileObj);
      if (!imagesToUpload.length) {
        return of(savePropertyImageSuccess("No new images to upload."));
      }

      const formData = new FormData();

      imagesToUpload.forEach((image) => {
        const file = image.originFileObj;
        if (file) {
          formData.append("files", file, file.name);
        }
      });

      if (formData.entries().next().done) {
        return of(savePropertyImageFailure("No valid files to upload."));
      }

      return observablePOST(`/property-image/${action.payload}`, {
        body: formData
      }).pipe(
        mergeMap(() => {
          return of(savePropertyImageSuccess("All images uploaded"));
        }),
        catchError((error) => {
          return of(savePropertyImageFailure(error));
        })
      );
    })
  );
};

const fetchDataPropertiesByUserEpic = (action$) =>
  action$.pipe(
    ofType(propertiesByUserRequest.type),
    mergeMap((action) => {
      const { userId, page = 0 } = action.payload;
      return observableGET(
        `/property/property-by-user/${userId}?page=${page}`
      ).pipe(
        mergeMap((response) => {
          const { properties, totalPages } = response.data;
          return of(
            propertiesByUserSuccess({
              properties,
              totalPages,
              currentPage: page
            })
          );
        }),
        catchError((error) =>
          of(
            propertiesByUserFailure({
              error: error.message || "Failed to fetch properties"
            })
          )
        )
      );
    })
  );

const fetchDataMyPropertiesEpic = (action$) =>
  action$.pipe(
    ofType(myPropertiesRequest.type),
    mergeMap((action) => {
      const { userId, page = 0 } = action.payload;
      return observableGET(`/property/my-property/${userId}?page=${page}`).pipe(
        mergeMap((response) => {
          const { properties, totalPages } = response.data;
          return of(
            myPropertiesSuccess({
              properties,
              totalPages,
              currentPage: page
            })
          );
        }),
        catchError((error) =>
          of(
            myPropertiesFailure({
              error: error.message || "Failed to fetch properties"
            })
          )
        )
      );
    })
  );

const deletePropertyEpic = (action$) =>
  action$.pipe(
    ofType(propertyDeleteRequest.type),
    mergeMap((action) => {
      const { propertyId } = action.payload;
      return observableDELETE(`/property/${propertyId}`).pipe(
        mergeMap((response) =>
          of(
            propertyDeleteSuccess(response.data),
            increaseTotalAvailableListings()
          )
        ),
        catchError((error) => of(propertyDeleteFailure(error)))
      );
    })
  );

const hidePropertyEpic = (action$) =>
  action$.pipe(
    ofType(propertyHideRequest.type),
    mergeMap((action) => {
      const { propertyId } = action.payload;
      return observablePATCH(`/property/${propertyId}`).pipe(
        mergeMap((response) => of(propertyHideSuccess(response.data))),
        catchError((error) => of(propertyHideFailure(error)))
      );
    })
  );

const editPropertyEpic = (action$) => {
  return action$.pipe(
    ofType(editPropertyRequest.type),
    mergeMap((action) => {
      return observablePUT(`/property`, {
        body: action.payload
      }).pipe(
        mergeMap((response) => {
          return of(
            editPropertySuccess(response.data),
            savePropertyImageRequest(action.payload.uuid)
          );
        }),
        catchError((error) => of(editPropertyFailure(error)))
      );
    })
  );
};
const aiSearchRequestEpic = (action$) => {
  return action$.pipe(
    ofType(aiSearchRequest.type),
    mergeMap((action) => {
      return observablePOST(
        `/ai-powered-property-search`,
        {
          body: action.payload
        },
        "ai"
      ).pipe(
        mergeMap((response) => {
          return of(aiSearchSuccess(response.data));
        }),
        catchError((error) => {
          return of(aiSearchFailure(error));
        })
      );
    })
  );
};

const deletePropertyImageEpic = (action$) =>
  action$.pipe(
    ofType(deletePropertyImageRequest.type),
    mergeMap((action) => {
      const { propertyImageId } = action.payload;
      return observableDELETE(`/property-image/${propertyImageId}`).pipe(
        mergeMap(() => {
          return of(deletePropertyImageSuccess({ propertyImageId }));
        }),
        catchError((error) => {
          return of(deletePropertyImageFailure(error));
        })
      );
    })
  );

const comparePropertiesEpic = (action$) => {
  return action$.pipe(
    ofType(getCompareProperties.type),
    mergeMap((action) => {
      return observablePOST(`/property/compare-property`, {
        body: action.payload
      }).pipe(
        mergeMap((response) => {
          return of(comparePropertiesSuccess(response.data));
        }),
        catchError((error) => of(comparePropertiesFailure(error)))
      );
    })
  );
};
const markPropertyAsFavoriteEpic = (action$) =>
  action$.pipe(
    ofType(markPropertyAsFavoriteRequest.type),
    mergeMap((action) => {
      const { propertyId, notApiCalled } = action.payload;
      if (!notApiCalled) {
        return observablePOST(`/favourites/${propertyId}`).pipe(
          mergeMap(() => {
            return of(markPropertyAsFavoriteSuccess({ propertyId }));
          }),
          catchError((error) => {
            return of(
              markPropertyAsFavoriteFailure(error),
              unMarkPropertyAsFavoriteRequest({
                propertyId,
                notApiCalled: true
              })
            );
          })
        );
      }
    })
  );

const unMarkPropertyAsFavoriteEpic = (action$) =>
  action$.pipe(
    ofType(unMarkPropertyAsFavoriteRequest.type),
    mergeMap((action) => {
      const { propertyId, notApiCalled } = action.payload;
      if (!notApiCalled) {
        return observableDELETE(`/favourites/${propertyId}`).pipe(
          mergeMap(() => {
            return of(unMarkPropertyAsFavoriteSuccess({ propertyId }));
          }),
          catchError((error) => {
            return of(
              unMarkPropertyAsFavoriteFailure(error),
              markPropertyAsFavoriteRequest({
                propertyId,
                notApiCalled: true
              })
            );
          })
        );
      }
    })
  );
const fetchDataFavouritePropertyEpic = (action$) =>
  action$.pipe(
    ofType(favouritePropertiesRequest.type),
    mergeMap((action) => {
      const { page = 0 } = action.payload;
      return observableGET(`/favourites/?page=${page}`).pipe(
        mergeMap((response) => {
          const { properties, totalPages } = response.data;
          return of(
            favouritePropertiesSuccess({
              properties,
              totalPages,
              currentPage: page
            })
          );
        }),
        catchError((error) => of(favouritePropertiesFailure(error)))
      );
    })
  );
const postDataAddCountAsPropertyViewEpic = (action$) =>
  action$.pipe(
    ofType(addCountAsPropertyViewRequest.type),
    mergeMap((action) => {
      const { propertyId } = action.payload;
      return observablePOST(
        `/property_view/property-add-views/${propertyId}`
      ).pipe(
        mergeMap((response) => {
          return of(addCountAsPropertyViewSuccess(response.data));
        }),
        catchError((error) => of(addCountAsPropertyViewFailure(error)))
      );
    })
  );

const fetchCountryBasedPropertyEpic = (action$) =>
  action$.pipe(
    ofType(countryBasedPropertyRequest.type),
    mergeMap(() => {
      return observableGET(`/property/country-based`).pipe(
        mergeMap((response) => of(countryBasedPropertySuccess(response.data))),
        catchError((error) => of(countryBasedPropertyFailure(error)))
      );
    })
  );

const propertyEpic = combineEpics(
  fetchDataPropertyEpic,
  fetchDataMyPropertiesEpic,
  fetchDataPropertySignatureEpic,
  fetchDataExplorePropertyEpic,
  fetchDataPropertyDetailEpic,
  fetchDataPropertyFilterEpic,
  fetchTagEpic,
  savePropertyEpic,
  savePropertyImageEpic,
  fetchDataPropertiesByUserEpic,
  deletePropertyEpic,
  hidePropertyEpic,
  editPropertyEpic,
  aiSearchRequestEpic,
  deletePropertyImageEpic,
  comparePropertiesEpic,
  markPropertyAsFavoriteEpic,
  unMarkPropertyAsFavoriteEpic,
  fetchDataFavouritePropertyEpic,
  postDataAddCountAsPropertyViewEpic,
  fetchCountryBasedPropertyEpic
);

export default propertyEpic;
